/**
 * Ops Slice 1 — client helper for the paperwork-attachments endpoint:
 *   uploadOpsPaperworkAttachment(dealId, paperworkItemId | null, file)
 *     → POST op=sign → uploadToSignedUrl → POST op=commit.
 *   deleteOpsPaperworkAttachment(attachmentId) → DELETE ?id=.
 *
 * Mirrors `src/lib/adsAssistantClientPostMedia.ts` shape, reusing `opsFetch`
 * from `opsPaperworkClientBase` for auth + workspace org header parity.
 */
import { opsFetch } from "@/lib/opsPaperworkClientBase";
import { requireSupabase } from "@/lib/supabase";

const ATTACHMENTS_BASE = "/api/ops/paperwork-attachments";

export type OpsPaperworkAttachment = {
  id: string;
  deal_id: string;
  paperwork_item_id: string | null;
  object_path: string;
  content_type: string;
  byte_size: number;
  display_name: string | null;
  sort_order: number;
  created_at: string;
  signed_url?: string;
  signed_expires_at?: string;
};

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  attachment_id: string;
  content_type: string;
};

type CommitOk = {
  ok: true;
  attachment: OpsPaperworkAttachment;
};

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.attachment_id === "string" &&
    typeof o.content_type === "string"
  );
}

function isCommitOk(x: unknown): x is CommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && o.attachment !== null && typeof o.attachment === "object";
}

export async function uploadOpsPaperworkAttachment(
  dealId: string,
  paperworkItemId: string | null,
  file: File,
): Promise<{ ok: true; attachment: OpsPaperworkAttachment } | { ok: false; error: string }> {
  const sb = requireSupabase();
  const ct = (file.type || "application/octet-stream").toLowerCase();

  const sign = await opsFetch<unknown>(ATTACHMENTS_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "sign",
      deal_id: dealId,
      paperwork_item_id: paperworkItemId,
      content_type: ct,
      byte_size: file.size,
      display_name: file.name,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) {
    const errCandidate = (sign.data as { error?: unknown })?.error;
    return { ok: false, error: typeof errCandidate === "string" ? errCandidate : "Sign response invalid" };
  }
  const s = sign.data;

  const { error: upErr } = await sb.storage.from(s.bucket).uploadToSignedUrl(s.path, s.token, file, {
    contentType: s.content_type,
    upsert: false,
  });
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await opsFetch<unknown>(ATTACHMENTS_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "commit",
      deal_id: dealId,
      paperwork_item_id: paperworkItemId,
      attachment_id: s.attachment_id,
      object_path: s.path,
      content_type: s.content_type,
      byte_size: file.size,
      display_name: file.name,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!isCommitOk(commit.data)) {
    const errCandidate = (commit.data as { error?: unknown })?.error;
    return { ok: false, error: typeof errCandidate === "string" ? errCandidate : "Commit response invalid" };
  }
  return { ok: true, attachment: commit.data.attachment };
}

export async function deleteOpsPaperworkAttachment(
  attachmentId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const url = `${ATTACHMENTS_BASE}?id=${encodeURIComponent(attachmentId)}`;
  const r = await opsFetch<{ ok?: boolean; error?: string }>(url, { method: "DELETE" });
  if (!r.ok) return { ok: false, error: r.error };
  return { ok: true };
}
